// Navigate an index after options (filters / sort / query) change.
// Nested turbo_index must stay inside its frame — never inherit the parent show URL.
import { clearSearchResultsSkeleton, skeletonizeSearchResults } from "../entrypoints/common"

function embeddedTurboFrame(element) {
  return element?.closest?.("turbo-frame[data-turbo-index-embedded]")
}

function embeddedIndexSrc(element) {
  const frame = embeddedTurboFrame(element)
  if (!frame) return null
  return frame.getAttribute("src") || frame.dataset.lazySrc || null
}

function buildIndexOptionsUrl({ path, options, clearQuery = false, element = null } = {}) {
  const embeddedFrame = embeddedTurboFrame(element)
  const url = new URL(path || window.location.pathname, window.location.origin)

  if (embeddedFrame) {
    const src = embeddedIndexSrc(element)
    if (src) {
      const srcUrl = new URL(src, window.location.origin)
      srcUrl.searchParams.forEach((value, key) => {
        if (key === "options") return
        if (clearQuery && key === "query") return
        url.searchParams.set(key, value)
      })
    }
  } else {
    const current = new URL(window.location.href)
    current.searchParams.forEach((value, key) => {
      if (key === "options") return
      if (clearQuery && key === "query") return
      url.searchParams.set(key, value)
    })
  }

  if (clearQuery) url.searchParams.delete("query")
  url.searchParams.delete("page")
  // `load=true` vaut pour les deux branches. Sans lui le serveur répond la coquille — une
  // frame paresseuse, sans aucun `#…_search_results` dedans — donc `fetchFrameContents` ne
  // trouvait jamais sa cible et TOUT filtre d'index racine retombait sur une visite pleine
  // page. Le formulaire de recherche donne la règle : son action porte déjà `load=true`,
  // et c'est le même point d'entrée.
  url.searchParams.set("load", "true")
  url.searchParams.set("options", JSON.stringify(options || {}))

  return url
}

function resolveSearchResultsFrame(element, searchResultsFrameId = null) {
  const embedded = embeddedTurboFrame(element)
  const scopedRoot = embedded || element?.closest?.(".turbo-index")

  if (searchResultsFrameId) {
    const selector = `#${CSS.escape(searchResultsFrameId)}`
    const scoped = scopedRoot?.querySelector(selector)
    if (scoped) return scoped

    // Un filtre rendu hors de l'index (modale d'export : elle vit dans le <body>) ne
    // trouve pas la frame par ancêtre ; son id, lui, désigne la liste de la page.
    // Plusieurs frames peuvent porter cet id (la recherche globale partage `search_results`
    // avec les listes sans namespace) : celle d'un index prime.
    const candidates = [...document.querySelectorAll(selector)]
    const global = candidates.find((frame) => frame.closest("[data-controller~='dashboard-index']")) || candidates[0]
    if (global) return global
  }

  if (!element) return null

  const root =
    embedded ||
    element.closest("[data-controller~='search']") ||
    element.closest("[data-controller~='dashboard-index']") ||
    element.closest(".turbo-index")

  if (root) {
    const scoped = root.querySelector("[id$='_search_results'], #search_results")
    if (scoped) return scoped
  }

  return element.closest("[id$='_search_results'], #search_results")
}

function extractFrameFromDocument(doc, frameId) {
  if (!doc || !frameId) return null

  const byId = doc.getElementById(frameId)
  if (byId) return byId

  return doc.querySelector(`turbo-frame#${CSS.escape(frameId)}`)
}

async function fetchFrameContents(frame, url, { responseFrameId = frame.id } = {}) {
  frame.setAttribute("busy", "")

  try {
    const response = await fetch(url.toString(), {
      headers: {
        Accept: "text/html",
        "Turbo-Frame": responseFrameId,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
    if (!response.ok) return false

    const html = await response.text()
    const doc = new DOMParser().parseFromString(html, "text/html")
    const newFrame = extractFrameFromDocument(doc, frame.id)
    if (!newFrame) return false

    frame.replaceChildren(...newFrame.children)
    frame.dispatchEvent(new Event("turbo:frame-load", { bubbles: true }))
    return true
  } finally {
    frame.removeAttribute("busy")
  }
}

// La frame d'index paresseuse mémorise dans `data-lazy-src` l'URL d'où vient ce qu'elle
// affiche (cf. freezeLazyFrame dans entrypoints/common). On vient d'en remplacer le corps
// par la réponse d'une AUTRE URL : sans mise à jour, la frame prétend toujours montrer la
// liste non filtrée, et revenir sur l'onglet Contrats regreffait le tableau filtré sous une
// URL propre.
//
// Comme l'historique, réservé à l'index qui possède l'URL de la page : c'est là qu'on sait
// que `url` rejoue exactement ce qui est affiché. L'index embarqué d'une show reconstruit
// ses options sans le contexte de sa ressource parente, son URL de filtre ne redonnerait pas
// la même liste.
function rememberIndexFrameSource(container, url, embeddedFrame) {
  if (embeddedFrame) return

  let frame = container?.closest?.("turbo-frame")
  // Sur les index en cartes, `…_search_results` est lui-même une frame ; celle qui porte le
  // `lazy-src` est la coquille d'index au-dessus.
  while (frame && !frame.dataset.lazySrc) frame = frame.parentElement?.closest("turbo-frame")
  if (frame) frame.dataset.lazySrc = url.toString()
}

// L'URL doit porter le filtre : sans ça un rechargement le perdrait, et c'était le seul
// retour visible tant que le remplacement en place échouait. Même règle que la recherche
// d'index (search_controller#loadSearchResults) : `replaceState` — un filtre se change comme
// on corrige une recherche, le bouton Retour doit ramener à la page PRÉCÉDENTE, pas défaire
// les filtres un par un — et seulement si l'on est bien sur la page de cet index. Un index
// embarqué dans une show n'a rien à écrire dans l'URL de la show.
function syncIndexOptionsHistory(url, embeddedFrame) {
  if (embeddedFrame) return

  const trim = (pathname) => pathname.replace(/\/$/, "")
  if (trim(url.pathname) !== trim(window.location.pathname)) return

  window.history.replaceState(window.history.state, "", url)
}

// Annonce qu'une liste vient d'être rechargée avec de nouvelles options. L'événement part du
// `document` : l'élément qui a déclenché le filtre (en-tête de colonne, pastille…) vit le plus
// souvent DANS la frame de résultats, que la réponse vient de remplacer. Émis depuis lui, il
// partirait d'un nœud détaché et n'atteindrait jamais ceux qui écoutent (la modale d'export).
// `frameId` permet à chacun de ne réagir qu'à sa liste quand plusieurs cohabitent (show).
function announceIndexOptionsChange({ options, frameId }) {
  document.dispatchEvent(
    new CustomEvent("index-options:changed", { detail: { options: options || {}, frameId: frameId || null } })
  )
}

export async function visitIndexWithOptions({
  path,
  options,
  clearQuery = false,
  element = null,
  searchResultsFrameId = null,
} = {}) {
  const url = buildIndexOptionsUrl({ path, options, clearQuery, element })
  const embeddedFrame = embeddedTurboFrame(element)
  const searchResultsFrame = resolveSearchResultsFrame(element, searchResultsFrameId)

  if (searchResultsFrame?.id) {
    // Le corps du tableau passe en squelette le temps de la réponse : la requête peut durer
    // près d'une seconde sur les gros index, et rien d'autre ne dit que le clic est parti.
    skeletonizeSearchResults(searchResultsFrame)

    // `finally` comme dans search_controller : une requête qui échoue ne doit pas laisser le
    // conteneur en `aria-busy` pour le reste de la vie de la page.
    let updated = false
    try {
      updated = await fetchFrameContents(searchResultsFrame, url, {
        responseFrameId: searchResultsFrame.id,
      })
    } finally {
      clearSearchResultsSkeleton(searchResultsFrame)
    }

    if (updated) {
      rememberIndexFrameSource(searchResultsFrame, url, embeddedFrame)
      syncIndexOptionsHistory(url, embeddedFrame)
      announceIndexOptionsChange({ options, frameId: searchResultsFrame.id })
      return
    }

    if (embeddedFrame?.id) {
      const frameUpdated = await fetchFrameContents(embeddedFrame, url, {
        responseFrameId: embeddedFrame.id,
      })
      if (frameUpdated) return
    }

    // Nested index on a show page — never promote to a full document visit.
    if (embeddedFrame) return
  }

  if (window.Turbo && embeddedFrame?.id) {
    window.Turbo.visit(url.toString(), { frame: embeddedFrame.id })
  } else if (window.Turbo) {
    window.Turbo.visit(url.toString(), { action: "replace" })
  } else {
    window.location.href = url.toString()
  }
}
